/**
 * Tooltip — shadcn/ui primitive (TruSpace).
 *
 * Radix-backed tooltip. Use for icon-only button labels and to explain
 * why a disabled control is disabled (read-only / permission states).
 *
 * Keep tooltip content SHORT — it is not a substitute for an
 * `aria-label` on icon buttons (a11y.mdc §3 still requires that). The
 * floating surface sits at `z-tooltip`, above menus and modals.
 *
 * Wrap the app (or a subtree) in a single <TooltipProvider> once; each
 * tooltip is then <Tooltip><TooltipTrigger/><TooltipContent/></Tooltip>.
 *
 * @see https://ui.shadcn.com/docs/components/tooltip
 */
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { ComponentPropsWithoutRef } from 'react';
import { cn } from '@/lib/cn';

/* eslint-disable react-refresh/only-export-components */
export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;
/* eslint-enable react-refresh/only-export-components */

export function TooltipContent({
  className,
  sideOffset = 4,
  ...props
}: ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          'z-tooltip max-w-xs overflow-hidden rounded-md border border-border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md',
          'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=delayed-open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
          'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
}
